import * as THREE from "three";
import { LevelSim } from "@highmarch/domain";
import type { VFX } from "../../../../art/vfx.js";
import { audio } from "../audio/audio";
import { Settings, reducedVfx } from "../ui/settings";
import { MortarShells } from "./views/mortarShells";
import { ArcaneBeams } from "./views/arcaneBeams";
import { HexWards } from "./views/hexWards";
import { BarracksPosts } from "./views/barracksPosts";
import { towerFireRate } from "./views/towerRhythm";
import { Tier4Fx } from "./tier4Fx";

/** The whistle's length (s, the sample): it starts this long before the landing so it ends on the burst. */
export const WHISTLE_SECONDS = 0.8;
/**
 * Seconds from a shell's release to its whistle starting, so the whistle ends on the landing: `flightSeconds` of real
 * time between release and landing (the sim's flight at the game speed). A flight shorter than the whistle starts it
 * at once (it is cut by the burst). Pure.
 */
export function whistleDelay(flightSeconds: number): number { return Math.max(0, flightSeconds - WHISTLE_SECONDS); }

/**
 * The TOWERS_V2 towers' battle presentation in one place (docs/TOWERS_V2.md): the Mortar's shells (views/
 * mortarShells.ts), the Arcane Spires' beams (views/arcaneBeams.ts), the Hex Totems' wards and curse marks (views/
 * hexWards.ts) and the Barracks' post pennants (views/barracksPosts.ts), with the sounds they own: the shell's whistle
 * (timed to end on the landing) and the spires' held hum. The level scene builds it, hooks it to the tower and enemy
 * views, and calls `onTick` after each sim tick and `update` every frame after the towers have posed. The tower clips
 * (the Mortar's kick, the Barracks gate, the spire's channel, the totem's pulse) are the tower views' (views/towers.ts).
 */
export class TowersV2Fx {
  readonly group = new THREE.Group();
  readonly shells: MortarShells;
  readonly beams: ArcaneBeams;
  readonly hexes: HexWards;
  readonly posts: BarracksPosts;
  /** Tier IV (M21): creatures, zones, auras and skill moments (tier4Fx.ts). */
  readonly tier4: Tier4Fx;
  /** Set by the level: the sprite VFX once loaded. */
  fx: () => VFX | null = () => null;

  constructor(private readonly sim: LevelSim, camera: () => THREE.Camera | null) {
    const fx = () => this.fx();
    this.shells = new MortarShells(sim, fx, reducedVfx);
    this.beams = new ArcaneBeams(sim, fx, reducedVfx, camera);
    this.hexes = new HexWards(sim, fx, reducedVfx);
    this.posts = new BarracksPosts(sim, reducedVfx);
    this.tier4 = new Tier4Fx(sim, reducedVfx);
    this.tier4.fx = fx;
    this.group.add(this.shells.group, this.beams.group, this.hexes.group, this.posts.group, this.tier4.group);
    this.shells.onRelease = (_s, flightSimSeconds) => {
      const real = flightSimSeconds / Math.max(1, Settings.current.speed);
      audio.play("mortarWhistle", whistleDelay(real));
    };
  }

  /** Hooks into the tower and enemy views (TowerViews.nodeWorld, EnemyViews.feet / drawnPoints). */
  attach(towerNode: (towerId: number, part: string) => THREE.Vector3 | null, enemyFeet: (id: number) => THREE.Vector3 | null, enemyBody: (id: number) => THREE.Vector3 | null, hideBody: (id: number, hidden: boolean) => void = () => {}): void {
    this.tier4.towerNode = towerNode;
    this.tier4.enemyFeet = enemyFeet;
    this.tier4.enemyBody = enemyBody;
    this.tier4.creatures.enemyFeet = enemyFeet;
    this.tier4.creatures.hideBody = hideBody;
    this.shells.towerNode = towerNode;
    this.beams.towerNode = towerNode;
    this.beams.enemyAt = enemyBody;
    this.hexes.towerNode = towerNode;
    this.hexes.enemyFeet = enemyFeet;
    this.hexes.enemyBody = enemyBody;
  }

  onTick(): void {
    this.shells.onTick((id) => { const t = this.sim.findTower(id); return t ? towerFireRate(this.sim, t) : 1; });
    this.beams.onTick();
    this.hexes.onTick();
    this.posts.onTick();
    this.tier4.onTick();
  }

  /** Per frame: `alpha` the tick fraction, `dt` the frame's sim seconds. */
  update(alpha: number, dt: number): void {
    this.shells.update(alpha, dt);
    this.beams.update(dt);
    this.hexes.update(dt);
    this.posts.update(dt);
    this.tier4.update(alpha, dt);
    const r = this.beams.holdRamp();
    audio.hum(r >= 0 ? 1 : 0, Math.max(0, r));
  }

  dispose(): void {
    audio.hum(0, 0);
    this.shells.dispose(); this.beams.dispose(); this.hexes.dispose(); this.posts.dispose(); this.tier4.dispose();
    this.group.clear();
  }
}
